import {
  Alert,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Autotractor } from "protos/atk/autotractor";
import { capitalize } from "common/utils/strings";
import { CarbonLoadingSpinner } from "portal/components/CarbonLoadingSpinner";
import { ConfigValidationResult } from "protos/portal/atk_equipment";
import { CreateJobsRequest } from "protos/rtc/jobs";
import { DimensionedNumberDisplayModule } from "../DimensionedNumberDisplayModule";
import { DryRunPreview } from "../DryRunPreview";
import { EquipmentLibraryCard } from "portal/components/atk/EquipmentLibraryCard";
import {
  formStateToCreateJobRequest,
  NewJobsFormState,
  rigInvalidReason,
} from "../FormState";
import { getPlantingHeading } from "common/utils/farm";
import { getRigLibraryCardData } from "portal/components/atk/RigLibraryCardData";
import { NewJobsWizardFormProps } from "../NewJobsWizard";
import { PassSettingsSection } from "./PassSettingsSection";
import { Path } from "portal/utils/routing";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { Trans, useTranslation } from "react-i18next";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { WizardStepButtonBar } from "../WizardStepButtonBar";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PreviewPathIcon from "portal/images/icons/conversion_path.svg?react";
import React, { useCallback, useMemo, useState } from "react";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";

const S = typedPath<NewJobsFormState>();

type GroundPrepConfigurePreviewFormProps = Pick<
  NewJobsWizardFormProps,
  | "customerId"
  | "farmMapData"
  | "farmIdByFieldId"
  | "rigSearchResult"
  | "rigsLoading"
>;

interface RigLibraryProps {
  rigs: Autotractor[] | undefined;
  rigsLoading: boolean;
  validationResults: Record<string, ConfigValidationResult> | undefined;
}

const RigLibrary: React.FC<RigLibraryProps> = ({
  rigs,
  rigsLoading,
  validationResults,
}) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const { setFieldValue, setFieldTouched, values } =
    useFormikContext<NewJobsFormState>();

  const sortedRigs = useMemo(() => {
    if (!rigs) {
      return;
    }
    return [...rigs].sort((a, b) => {
      const aInvalid = rigInvalidReason(
        a,
        values.jobType,
        validationResults?.[a.id],
        t
      );
      const bInvalid = rigInvalidReason(
        b,
        values.jobType,
        validationResults?.[b.id],
        t
      );
      if (aInvalid && !bInvalid) {
        return 1;
      }
      if (!aInvalid && bInvalid) {
        return -1;
      }
      return 0;
    });
  }, [rigs, values.jobType, validationResults, t]);

  if (rigsLoading) {
    return <CarbonLoadingSpinner />;
  } else if (sortedRigs && sortedRigs.length > 0) {
    return (
      <div className="grid grid-flow-row grid-cols-4 gap-2">
        {sortedRigs.map((rig) => {
          const invalidReason = rigInvalidReason(
            rig,
            values.jobType,
            validationResults?.[rig.id],
            t
          );
          const isSelected = values.previewSelectedRigId === rig.id;

          let cardClassName = "";
          if (invalidReason) {
            cardClassName = "opacity-50 cursor-default";
          } else if (isSelected) {
            cardClassName = "border-2 border-solid border-carbon-orange";
          }

          const card = (
            <EquipmentLibraryCard
              key={rig.id}
              data={getRigLibraryCardData(t, undefined, rig, measurementSystem)}
              variant="small-no-image"
              onCardClick={
                invalidReason
                  ? undefined
                  : () => {
                      setFieldValue(S.previewSelectedRigId.$path, rig.id);
                      setFieldTouched(S.previewSelectedRigId.$path, true);
                    }
              }
              className={cardClassName}
            />
          );

          if (invalidReason) {
            return (
              <Tooltip key={rig.id} title={invalidReason} arrow>
                <div>{card}</div>
              </Tooltip>
            );
          }

          return card;
        })}
      </div>
    );
  } else {
    return (
      <Alert severity="error" className="w-full text-base">
        <Trans
          t={t}
          components={{
            fleetLink: <Link href={Path.FLEET} />,
          }}
          i18nKey="views.autotractor.jobCreation.step3ConfigurePreview.noSetups"
        />
      </Alert>
    );
  }
};

export const GroundPrepConfigurePreviewForm: React.FC<
  GroundPrepConfigurePreviewFormProps
> = ({
  customerId,
  farmMapData,
  farmIdByFieldId,
  rigSearchResult,
  rigsLoading,
}) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const { values, isValid } = useFormikContext<NewJobsFormState>();
  const [doDryRuns, dryRunResult] = rtcJobsApi.useCreateBulkJobsMutation({});

  const [viewingDryRunIndex, setViewingDryRunIndex] = useState(0);
  const nFailedDryRuns = useMemo(() => {
    const dryRunJobs = dryRunResult.data?.jobs ?? [];
    return dryRunJobs.filter((job) => job.creationFailed).length;
  }, [dryRunResult.data?.jobs]);
  const viewingDryRunJob = useMemo(() => {
    const dryRunJobs = dryRunResult.data?.jobs ?? [];
    return dryRunJobs[viewingDryRunIndex];
  }, [dryRunResult.data?.jobs, viewingDryRunIndex]);
  const viewingDryRunFarm = useMemo(() => {
    return viewingDryRunJob?.farmId
      ? farmMapData[viewingDryRunJob.farmId]
      : undefined;
  }, [farmMapData, viewingDryRunJob?.farmId]);
  const viewingDryRunField = useMemo(() => {
    return viewingDryRunFarm?.zonesById[viewingDryRunJob?.fieldId ?? ""];
  }, [viewingDryRunFarm?.zonesById, viewingDryRunJob?.fieldId]);

  const onClickPreview = useCallback(() => {
    setViewingDryRunIndex(0);
    const bulkJobReq: CreateJobsRequest = { jobRequests: [] };

    for (const fieldId of values.selectedFieldIds) {
      const farmId = farmIdByFieldId[fieldId];
      const farm = farmId ? farmMapData[farmId] : undefined;

      if (!values.previewSelectedRigId) {
        throw new Error("onClickPreview invoked with no rig selected");
      }

      if (!farm) {
        throw new Error(
          `GroundPrepConfigurePreviewForm.onClickPreview: failed to get map data for ${fieldId}`
        );
      }

      bulkJobReq.jobRequests = formStateToCreateJobRequest(
        farm,
        // not localized because this string is only visible to carbon users
        () => `create jobs preview ${Date.now()}`,
        customerId,
        fieldId,
        true, // use dry runs for preview
        values,
        { rigIds: [values.previewSelectedRigId] }
      );
    }

    doDryRuns(bulkJobReq);
  }, [customerId, doDryRuns, farmIdByFieldId, farmMapData, values]);

  const plantingHeadingOffsetEnabled = useMemo(() => {
    for (const fieldId of values.selectedFieldIds) {
      const farmId = farmIdByFieldId[fieldId];

      if (!farmId) {
        throw new Error(
          `plantingHeadingOffsetDisabled: fieldId ${fieldId} has no farm?`
        );
      }

      const farm = farmMapData[farmId];

      if (!farm) {
        throw new Error(
          `plantingHeadingOffsetDisabled: no map data for ${farmId}`
        );
      }

      const field = farm.zonesById[fieldId];

      if (!field) {
        throw new Error(
          `plantingHeadingOffsetDisabled: couldn't get zone ${fieldId}`
        );
      }

      const plantingHeading = getPlantingHeading(field, farm.pointsById);

      if (!plantingHeading) {
        // can't base the row heading on a planting heading if any field is
        // selected with an unknown planting heading
        return false;
      }
    }

    return true;
  }, [farmIdByFieldId, farmMapData, values.selectedFieldIds]);

  let previewSection: JSX.Element | undefined;

  if (dryRunResult.isError) {
    // network request failed
    previewSection = (
      <Alert severity="warning" className="w-full text-base rounded-lg">
        {t(
          "views.autotractor.jobCreation.step3ConfigurePreview.networkFailureAlert"
        )}
      </Alert>
    );
  } else if (dryRunResult.isSuccess) {
    // network request succeeded; we can display a preview (even if creating the
    // job itself failed)
    previewSection = (
      <>
        <Typography>
          {t("views.autotractor.jobCreation.step3ConfigurePreview.pathPreview")}
        </Typography>
        <Card className="rounded-lg p-4 w-full flex flex-row items-center justify-between">
          <div className="flex flex-col">
            <Typography>{viewingDryRunField?.name ?? ""}</Typography>
            <Typography variant="body2">
              {viewingDryRunFarm?.name ?? ""}
            </Typography>
          </div>
          <div className="flex flex-col gap-2 items-center">
            <div className="flex flex-row items-center">
              <IconButton
                disabled={viewingDryRunIndex === 0}
                onClick={() => setViewingDryRunIndex(viewingDryRunIndex - 1)}
              >
                <ArrowLeftIcon />
              </IconButton>
              <Typography>
                {viewingDryRunIndex + 1}/{dryRunResult.data.jobs.length}
              </Typography>
              <IconButton
                disabled={
                  viewingDryRunIndex === dryRunResult.data.jobs.length - 1
                }
                onClick={() => setViewingDryRunIndex(viewingDryRunIndex + 1)}
              >
                <ArrowRightIcon />
              </IconButton>
            </div>
            {nFailedDryRuns > 0 && (
              <div className="flex flex-row gap-2 items-center bg-warning p-2 rounded-md">
                <WarningIcon fontSize="inherit" />
                {nFailedDryRuns}
              </div>
            )}
          </div>
        </Card>
        {viewingDryRunJob && (
          <DryRunPreview
            dryRun={viewingDryRunJob}
            showJobData={"task"}
            farmMapData={viewingDryRunFarm}
            configValueDisplayModules={[
              <DimensionedNumberDisplayModule
                key="row-heading"
                title={capitalize(t("models.pathPlanning.rowHeading"))}
                storedValue={
                  viewingDryRunJob.config?.groundPrep?.pathPlan
                    ?.rowHeadingDeg ?? 0
                }
                storedUnit="deg"
                displayUnits={{ imperial: "deg", metric: "deg" }}
                localSystem={measurementSystem}
                roundDisplayToNPlaces={1}
              />,
            ]}
          />
        )}
      </>
    );
  }

  // you should be able to continue to the next step without previewing, but
  // not if you don't have any rigs.
  const disableNext = !rigSearchResult || rigSearchResult.rigs.length === 0;

  return (
    <>
      <WizardStepButtonBar disableNext={disableNext} />
      <Paper
        variant="outlined"
        className="bg-inherit p-4 flex flex-col gap-4 rounded-lg"
      >
        <Typography variant="h5">
          {t("views.autotractor.jobCreation.step3ConfigurePreview.title")}
        </Typography>
        <Typography color="textSecondary">
          {t(
            "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.description"
          )}
        </Typography>
        <hr className="w-full" />
        <PassSettingsSection
          enablePlantingHeadingOffset={plantingHeadingOffsetEnabled}
        />
        <Typography>
          {t(
            "views.autotractor.jobCreation.step3ConfigurePreview.selectEquipmentSetup"
          )}
        </Typography>
        <RigLibrary
          rigs={rigSearchResult?.rigs}
          rigsLoading={rigsLoading}
          validationResults={rigSearchResult?.validationResults}
        />
        <div className="flex flex-row items-center gap-4">
          {dryRunResult.isLoading ? (
            <Button
              variant="contained"
              startIcon={
                <CircularProgress variant="indeterminate" className="w-6 h-6" />
              }
              className="w-1/5"
              disabled={true}
            >
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.generatingPath"
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<PreviewPathIcon />}
              className="w-1/5"
              disabled={!isValid || !values.previewSelectedRigId}
              onClick={onClickPreview}
            >
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.previewPath"
              )}
            </Button>
          )}
          <Typography variant="body2">
            {t(
              "views.autotractor.jobCreation.step3ConfigurePreview.pathPreviewSlowNote"
            )}
          </Typography>
        </div>
        {previewSection}
      </Paper>
      <WizardStepButtonBar disableNext={disableNext} />
    </>
  );
};
